import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { EntityFormPage } from "@/components/entity-page/EntityFormPage";
import EditClientSettingsForm from "@/components/EditClientSettingsForm";
import { fetchClient } from "@/lib/api";
import type { SerializedBusiness } from "@/types";

export const Route = createFileRoute("/dashboard/clients/$id/settings/")({
  component: ClientSettingsPage,
});

function ClientSettingsPage() {
  const { id } = Route.useParams();
  return <ClientSettingsPageComponent businessId={id} />;
}

// Exported for testing
export function ClientSettingsPageComponent({
  businessId: id,
}: {
  businessId: string;
}) {
  const navigate = useNavigate();
  const [client, setClient] = useState<SerializedBusiness | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  useEffect(() => {
    let active = true;
    fetchClient(id)
      .then((response) => {
        if (!active) return;
        setClient(response.client);
        setLoading(false);
      })
      .catch((error) => {
        if (!active) return;
        console.error("Error fetching client:", error);
        setNotFound(true);
        setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [id]);

  const goToClient = () =>
    navigate({ to: "/dashboard/clients/$id", params: { id } });
  const goToClientsList = () => navigate({ to: "/dashboard/clients" });

  if (loading) {
    return (
      <div className="p-8">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-center min-h-[400px]">
            <div>Loading...</div>
          </div>
        </div>
      </div>
    );
  }

  if (notFound || !client) {
    return (
      <div className="p-8">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-center min-h-[400px]">
            <div>Client not found</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <EntityFormPage
      title="Client Settings"
      description="Update client information or delete the client and all associated data."
      backHref={`/dashboard/clients/${id}`}
      headerActions={
        !showDeleteConfirm ? (
          <Button
            type="button"
            variant="destructive"
            onClick={() => setShowDeleteConfirm(true)}
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Delete Client
          </Button>
        ) : undefined
      }
    >
      <EditClientSettingsForm
        client={client}
        showDeleteConfirm={showDeleteConfirm}
        onCancelDelete={() => setShowDeleteConfirm(false)}
        onDeleted={goToClientsList}
        onSaved={goToClient}
      />
    </EntityFormPage>
  );
}
